import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import SchemaOrg from '@/components/base/SchemaOrg';

const FORM_SUBMIT_ADDR = 'https://readdy.ai/api/form/d9sp96tlbrt1qcgaj7eg';

const milestones = [
  { year: '2018', title: '品牌创立', description: 'NG南宫品牌正式成立，致力于打造优质游戏平台' },
  { year: '2019', title: '首款游戏上线', description: '首款休闲游戏上线，获得玩家热烈反响' },
  { year: '2020', title: '用户突破500万', description: '平台注册用户突破500万，成为行业新锐力量' },
  { year: '2022', title: '产品线扩展', description: '游戏品类扩展至五大类别，覆盖休闲、RPG、策略等' },
  { year: '2024', title: '全球化布局', description: '业务拓展至海外市场，累计下载量突破5000万' },
  { year: '2026', title: '全新升级', description: '平台全面升级，推出全新版本，用户体验大幅提升' },
];

const breadcrumbItems = [
  { label: '首页', path: '/' },
  { label: '关于我们' },
];

export default function AboutPage() {
  const [formState, setFormState] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypotEl = form.querySelector<HTMLInputElement>('input[name="company_alt"]');
    if (honeypotEl && honeypotEl.value.trim() !== '') {
      setFormState('success');
      setFormMessage('感谢您的留言，我们会尽快回复！');
      return;
    }

    setFormState('loading');
    try {
      const formData = new FormData(form);
      const response = await fetch(FORM_SUBMIT_ADDR, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormState('success');
        setFormMessage('感谢您的留言，我们会尽快回复！');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || '提交失败，请稍后重试';
        setFormState('error');
        setFormMessage(serverMsg);
      }
    } catch {
      setFormState('error');
      setFormMessage('网络错误，请检查网络连接后重试');
    }
  };

  const organizationSchema = {
    name: 'NG南宫',
    url: 'https://ngnangong.com',
    description: 'NG南宫官方正版APP下载平台，提供最新版本iOS/Android安装包。',
    foundingDate: '2018',
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '+86-400-888-8888',
      contactType: 'customer service',
      availableLanguage: ['Chinese'],
    },
  };

  const webpageSchema = {
    name: '关于我们 - NG南宫',
    description: '了解NG南宫品牌故事、发展历程、企业信誉和联系方式。',
    url: 'https://ngnangong.com/about',
  };

  return (
    <>
      <SchemaOrg type="Organization" data={organizationSchema} />
      <SchemaOrg type="WebPage" data={webpageSchema} />

      <Navbar />

      <main className="pt-16">
        <div className="bg-background-100 border-b border-secondary-200/60">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb items={breadcrumbItems} />
            <div className="pb-6">
              <h1 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-2">关于我们</h1>
              <p className="text-sm text-foreground-500">了解NG南宫的品牌故事与发展历程</p>
            </div>
          </div>
        </div>

        {/* Brand Introduction */}
        <section className="py-14 md:py-18 bg-background-50">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <h2 className="font-heading text-xl md:text-2xl text-foreground-900 mb-5 text-center">品牌介绍</h2>
            <div className="space-y-4 text-sm text-foreground-600 leading-relaxed">
              <p>
                <strong className="text-foreground-900">NG南宫</strong>是一家专注于移动游戏分发与运营的互联网科技品牌，成立于2018年。我们致力于为全球玩家提供安全、便捷、高品质的游戏下载与体验服务。
              </p>
              <p>
                NG南宫下载链接官方入口始终坚持"安全第一、用户至上"的核心理念，所有平台游戏均经过严格的安全检测和官方授权认证。我们自主研发的安全检测系统确保每一款游戏都不含病毒和恶意代码，让玩家可以放心下载，安心畅玩。
              </p>
              <p>
                经过多年发展，NG南宫已建立起涵盖休闲益智、角色扮演、策略竞技、棋牌游戏、体育竞速等五大游戏品类的丰富产品矩阵，累计服务超过2000万活跃用户，总下载量突破5000万次。
              </p>
              <p>
                展望未来，NG南宫将继续深耕游戏领域，不断优化产品体验，拓展更多优质游戏内容，努力成为全球领先的游戏分发平台，为每一位玩家带来极致的娱乐体验。
              </p>
            </div>
          </div>
        </section>

        {/* Milestones */}
        <section className="py-14 md:py-18 bg-background-100">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <h2 className="font-heading text-xl md:text-2xl text-foreground-900 mb-10 text-center">发展历程</h2>
            <div className="relative">
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-secondary-200 -translate-x-1/2 hidden md:block"></div>
              <div className="flex flex-col gap-8">
                {milestones.map((mile, index) => (
                  <div
                    key={mile.year}
                    className={`flex flex-col md:flex-row items-start gap-4 md:gap-8 ${
                      index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                    }`}
                  >
                    <div className={`flex-1 ${index % 2 === 0 ? 'md:text-right' : 'md:text-left'} hidden md:block`}>
                      {index % 2 === 0 && (
                        <div className="p-4 rounded-lg bg-background-50">
                          <h3 className="font-heading text-base text-foreground-900 mb-1">{mile.title}</h3>
                          <p className="text-xs text-foreground-500">{mile.description}</p>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-3 md:justify-center flex-shrink-0">
                      <span className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-500 text-background-50 text-xs font-bold">
                        {index + 1}
                      </span>
                    </div>
                    <div className={`flex-1 ${index % 2 !== 0 ? 'md:text-left' : 'md:text-right'} hidden md:block`}>
                      {index % 2 !== 0 && (
                        <div className="p-4 rounded-lg bg-background-50">
                          <h3 className="font-heading text-base text-foreground-900 mb-1">{mile.title}</h3>
                          <p className="text-xs text-foreground-500">{mile.description}</p>
                        </div>
                      )}
                    </div>
                    <div className="md:hidden pl-4 border-l-2 border-primary-300">
                      <span className="text-xs text-primary-500 font-medium">{mile.year}</span>
                      <h3 className="font-heading text-base text-foreground-900 mt-1">{mile.title}</h3>
                      <p className="text-xs text-foreground-500 mt-0.5">{mile.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Reputation */}
        <section className="py-14 md:py-18 bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <h2 className="font-heading text-xl md:text-2xl text-foreground-900 mb-10 text-center">企业信誉</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
              {[
                { icon: 'ri-shield-star-line', title: '安全认证', desc: '通过国际权威安全检测认证，所有游戏经过多重安全扫描，确保无毒无害' },
                { icon: 'ri-award-line', title: '行业认可', desc: '多次获得游戏行业年度最佳平台奖，受到业界和玩家的广泛认可' },
                { icon: 'ri-thumb-up-line', title: '用户口碑', desc: '2000万+用户信赖之选，App Store评分4.8分，好评率超过98%' },
              ].map((item) => (
                <div key={item.title} className="text-center p-6 rounded-lg bg-background-100">
                  <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-accent-100 text-accent-500 mb-4">
                    <i className={`${item.icon} text-lg`}></i>
                  </div>
                  <h3 className="font-heading text-base text-foreground-900 mb-2">{item.title}</h3>
                  <p className="text-xs text-foreground-500 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact */}
        <section className="py-14 md:py-18 bg-background-100">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <h2 className="font-heading text-xl md:text-2xl text-foreground-900 mb-10 text-center">联系我们</h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-5">
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-100 text-primary-500 flex-shrink-0">
                    <i className="ri-customer-service-2-line"></i>
                  </div>
                  <div>
                    <h3 className="font-heading text-sm text-foreground-900 mb-1">客服热线</h3>
                    <p className="text-xs text-foreground-500">400-888-8888</p>
                    <p className="text-xs text-foreground-400">7×24小时全天候服务</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-100 text-primary-500 flex-shrink-0">
                    <i className="ri-mail-line"></i>
                  </div>
                  <div>
                    <h3 className="font-heading text-sm text-foreground-900 mb-1">电子邮箱</h3>
                    <p className="text-xs text-foreground-500">support@ngnangong.com</p>
                    <p className="text-xs text-foreground-400">工作日24小时内回复</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-100 text-primary-500 flex-shrink-0">
                    <i className="ri-map-pin-line"></i>
                  </div>
                  <div>
                    <h3 className="font-heading text-sm text-foreground-900 mb-1">公司地址</h3>
                    <p className="text-xs text-foreground-500">中国广东省深圳市南山区科技园南路88号</p>
                    <p className="text-xs text-foreground-400">NG南宫科技大厦18层</p>
                  </div>
                </div>
              </div>

              <div className="bg-background-50 rounded-lg p-5">
                <h3 className="font-heading text-base text-foreground-900 mb-4">在线留言</h3>
                <form data-readdy-form="true" onSubmit={handleSubmit} className="flex flex-col gap-3">
                  <input
                    type="text"
                    name="name"
                    placeholder="您的姓名"
                    required
                    className="w-full px-3 py-2.5 rounded-md bg-background-100 border border-secondary-200/60 text-sm text-foreground-800 placeholder:text-foreground-300 outline-none focus:border-primary-300 transition-colors"
                  />
                  <input
                    type="email"
                    name="email"
                    placeholder="电子邮箱"
                    required
                    className="w-full px-3 py-2.5 rounded-md bg-background-100 border border-secondary-200/60 text-sm text-foreground-800 placeholder:text-foreground-300 outline-none focus:border-primary-300 transition-colors"
                  />
                  <div className="honeypot-field">
                    <input type="text" name="company_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                  </div>
                  <textarea
                    name="message"
                    placeholder="请输入您的留言（500字以内）"
                    rows={4}
                    maxLength={500}
                    required
                    className="w-full px-3 py-2.5 rounded-md bg-background-100 border border-secondary-200/60 text-sm text-foreground-800 placeholder:text-foreground-300 outline-none focus:border-primary-300 transition-colors resize-none"
                  ></textarea>
                  <button
                    type="submit"
                    disabled={formState === 'loading'}
                    className="w-full py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60"
                  >
                    {formState === 'loading' ? '提交中...' : '提交留言'}
                  </button>
                  {formState !== 'idle' && formState !== 'loading' && (
                    <p className={`text-xs ${formState === 'success' ? 'text-primary-600' : 'text-red-500'}`}>
                      {formMessage}
                    </p>
                  )}
                </form>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}